import { useEffect, useState, useMemo } from "react";
import {
  Sparkles, RefreshCw, Send, Clock, AlertTriangle, TrendingUp,
  Users, DollarSign, Activity, Award, ChevronRight, Loader2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";

const RISK_STYLES = {
  high: "bg-red-500/15 text-red-400 border-red-500/30",
  medium: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  low: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
};
const RISK_LABEL = { high: "Yüksek", medium: "Orta", low: "Düşük" };

function KpiCard({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "text-foreground",
    good: "text-emerald-500",
    warn: "text-amber-500",
    bad: "text-red-500",
    info: "text-blue-500",
  };
  return (
    <div className="glass-card rounded-xl p-5 border border-border/50" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
      </div>
      <div className={`font-display text-3xl font-bold tracking-tight tabular-nums ${tones[tone]}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function EmployeeRow({ e, metric, unit }) {
  const initials = (e.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border/40 last:border-0">
      {e.photo_url ? (
        <img src={e.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-bold">
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{e.name}</div>
        <div className="text-xs text-muted-foreground truncate">{e.department}</div>
      </div>
      <div className="text-right">
        <div className="font-mono font-semibold text-sm tabular-nums">
          {typeof metric === "number" ? metric.toLocaleString("tr-TR") : metric}
          {unit && <span className="text-xs text-muted-foreground ml-1">{unit}</span>}
        </div>
      </div>
    </div>
  );
}

function ScoreBar({ score }) {
  const color = score >= 80 ? "bg-emerald-500" : score >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2 w-28">
      <div className="flex-1 h-1.5 rounded-full bg-border/50 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${score}%` }} />
      </div>
      <span className="font-mono text-xs font-semibold tabular-nums w-8 text-right">{score}</span>
    </div>
  );
}

// Very tiny markdown renderer for AI output: **bold**, headings (1.), lists
function renderInsights(text) {
  if (!text) return null;
  const html = text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground">$1</strong>')
    .replace(/^### (.+)$/gm, '<h3 class="font-display text-base font-semibold mt-4 mb-1">$1</h3>')
    .replace(/^## (.+)$/gm, '<h3 class="font-display text-base font-semibold mt-4 mb-1">$1</h3>')
    .replace(/\n/g, "<br/>");
  return <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function AIInsights() {
  const [analytics, setAnalytics] = useState(null);
  const [aiText, setAiText] = useState(null);
  const [model, setModel] = useState("");
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingAi, setLoadingAi] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const { data } = await api.get("/ai/analytics");
      setAnalytics(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const generateAi = async () => {
    setLoadingAi(true);
    setAiText(null);
    try {
      const { data } = await api.get("/ai/insights");
      setAiText(data.insights);
      setModel(data.model || "");
      if (data.analytics) setAnalytics(data.analytics);
      toast.success("AI analiz üretildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingAi(false);
    }
  };

  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const { data } = await api.post("/ai/ask", { question });
      setAnswer(data.answer);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAsking(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const t = analytics?.totals || {};
  const otChange = t.overtime_change_pct;
  const otChangeTone = otChange == null ? "default" : otChange > 10 ? "bad" : otChange < -5 ? "good" : "warn";
  const otChangeSub =
    otChange == null
      ? "geçen ay verisi yok"
      : `${otChange > 0 ? "+" : ""}${otChange}% geçen aya göre`;

  const suggestedQuestions = useMemo(
    () => [
      "En çok geç kalan personel kim ve hangi saatte geliyor?",
      "Hangi departmanda devamsızlık en yüksek?",
      "Fazla mesai maliyetini nasıl düşürebiliriz?",
      "Cumartesi günleri için ek personele ihtiyaç var mı?",
    ],
    []
  );

  return (
    <div className="space-y-6" data-testid="ai-page">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-blue-500" /> AI Analiz
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Son 30 gün · Personel devam ve performans içgörüleri
            {model && <span className="ml-2 font-mono text-xs opacity-70">· {model}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadAnalytics}
            disabled={loadingAnalytics}
            className="gap-2"
            data-testid="refresh-analytics"
          >
            <RefreshCw className={`w-4 h-4 ${loadingAnalytics ? "animate-spin" : ""}`} />
            Verileri Yenile
          </Button>
          <Button onClick={generateAi} disabled={loadingAi} className="gap-2" data-testid="refresh-insights">
            {loadingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loadingAi ? "Üretiliyor…" : "AI Analiz Üret"}
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          icon={Users}
          label="Aktif Personel"
          value={t.active_employees ?? "—"}
          sub={`${analytics?.working_days_in_window ?? 0} çalışma günü`}
          tone="info"
          testid="kpi-active"
        />
        <KpiCard
          icon={Award}
          label="Ort. Performans"
          value={t.avg_score != null ? `${t.avg_score}` : "—"}
          sub="100 üzerinden"
          tone={t.avg_score >= 80 ? "good" : t.avg_score >= 60 ? "warn" : "bad"}
          testid="kpi-score"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Yüksek Risk"
          value={t.high_risk_count ?? "—"}
          sub="devamsızlık riski"
          tone={t.high_risk_count > 0 ? "bad" : "good"}
          testid="kpi-risk"
        />
        <KpiCard
          icon={DollarSign}
          label="Fazla Mesai Maliyeti"
          value={
            t.overtime_cost_estimate != null
              ? `${Math.round(t.overtime_cost_estimate).toLocaleString("tr-TR")} ₺`
              : "—"
          }
          sub={otChangeSub}
          tone={otChangeTone}
          testid="kpi-ot-cost"
        />
      </div>

      {/* AI narrative */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-xl p-6 border border-border/50"
        data-testid="insights-panel"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-500" />
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Yapay Zeka Yorumu</div>
          </div>
          {aiText && (
            <Badge variant="outline" className="text-xs">
              Son 30 gün
            </Badge>
          )}
        </div>
        {loadingAi && (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> AI verileri analiz ediyor…
          </div>
        )}
        {!loadingAi && !aiText && (
          <div className="text-sm text-muted-foreground py-4">
            Yukarıdaki <span className="font-semibold text-foreground">AI Analiz Üret</span> butonuna basarak
            personel devam verilerinin Claude Sonnet 4.6 tarafından yorumlanmasını sağlayabilirsiniz.
          </div>
        )}
        {aiText && renderInsights(aiText)}
      </motion.div>

      {/* Top lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass-card rounded-xl p-5 border border-border/50" data-testid="panel-late">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-amber-500" />
            <div className="font-display font-semibold text-sm tracking-tight">En Sık Geç Kalanlar</div>
          </div>
          {analytics?.top_late?.length ? (
            analytics.top_late.map((e) => (
              <EmployeeRow key={e.id} e={e} metric={`${e.late_days} gün / ${e.late_minutes} dk`} />
            ))
          ) : (
            <div className="text-xs text-muted-foreground py-4">Geç kalma kaydı yok.</div>
          )}
        </div>

        <div className="glass-card rounded-xl p-5 border border-border/50" data-testid="panel-overtime">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <div className="font-display font-semibold text-sm tracking-tight">En Yüksek Fazla Mesai</div>
          </div>
          {analytics?.top_overtime?.length ? (
            analytics.top_overtime.map((e) => (
              <EmployeeRow
                key={e.id}
                e={e}
                metric={
                  e.overtime_cost > 0
                    ? `${e.overtime_minutes} dk · ${Math.round(e.overtime_cost)} ₺`
                    : `${e.overtime_minutes} dk`
                }
              />
            ))
          ) : (
            <div className="text-xs text-muted-foreground py-4">Fazla mesai yok.</div>
          )}
        </div>

        <div className="glass-card rounded-xl p-5 border border-border/50" data-testid="panel-absent">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <div className="font-display font-semibold text-sm tracking-tight">Devamsızlık Riski</div>
          </div>
          {analytics?.top_absent?.length ? (
            analytics.top_absent.map((e) => (
              <div key={e.id} className="flex items-center gap-3 py-2.5 border-b border-border/40 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{e.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {e.absent_days} gün · %{Math.round(e.absent_ratio * 100)}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-md border ${RISK_STYLES[e.risk]}`}>
                  {RISK_LABEL[e.risk]}
                </span>
              </div>
            ))
          ) : (
            <div className="text-xs text-muted-foreground py-4">Risk grubu tespit edilmedi.</div>
          )}
        </div>
      </div>

      {/* Peaks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="glass-card rounded-xl p-5 border border-border/50" data-testid="panel-peak-hours">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-blue-500" />
            <div className="font-display font-semibold text-sm tracking-tight">En Yoğun Giriş Saatleri</div>
          </div>
          <div className="space-y-2">
            {analytics?.peak_hours?.length ? (
              analytics.peak_hours.map((h) => {
                const max = analytics.peak_hours[0].count || 1;
                return (
                  <div key={h.hour} className="flex items-center gap-3">
                    <div className="font-mono text-sm w-14 tabular-nums">
                      {String(h.hour).padStart(2, "0")}:00
                    </div>
                    <div className="flex-1 h-2 rounded-full bg-border/50 overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${(h.count / max) * 100}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground w-10 text-right tabular-nums">
                      {h.count}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-muted-foreground py-4">Yeterli veri yok.</div>
            )}
          </div>
        </div>

        <div className="glass-card rounded-xl p-5 border border-border/50" data-testid="panel-peak-days">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-emerald-500" />
            <div className="font-display font-semibold text-sm tracking-tight">En Yoğun Günler</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {analytics?.peak_days?.length ? (
              analytics.peak_days.map((d) => (
                <div key={d.day} className="rounded-lg bg-secondary/50 p-3 text-center">
                  <div className="font-display font-semibold text-sm">{d.day}</div>
                  <div className="font-mono text-2xl font-bold text-emerald-500 tabular-nums">{d.count}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">giriş</div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-xs text-muted-foreground py-4">Yeterli veri yok.</div>
            )}
          </div>
        </div>
      </div>

      {/* Performance leaderboard */}
      {analytics?.employees?.length > 0 && (
        <div className="glass-card rounded-xl p-5 border border-border/50" data-testid="panel-leaderboard">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-amber-500" />
            <div className="font-display font-semibold text-sm tracking-tight">
              Performans Sıralaması (Top {Math.min(10, analytics.employees.length)})
            </div>
          </div>
          <div>
            {analytics.employees.slice(0, 10).map((e, i) => (
              <div
                key={e.id}
                className="flex items-center gap-3 py-2.5 border-b border-border/40 last:border-0"
              >
                <div className="w-6 text-center text-xs font-mono font-bold text-muted-foreground">
                  {i + 1}
                </div>
                {e.photo_url ? (
                  <img src={e.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-bold">
                    {e.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{e.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {e.department} · {e.days_present} gün, {e.worked_hours} sa
                  </div>
                </div>
                <ScoreBar score={e.score} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Q&A */}
      <div className="glass-card rounded-xl p-6 border border-border/50" data-testid="ai-ask">
        <div className="font-display font-semibold text-lg mb-1 tracking-tight">AI&apos;ya Soru Sor</div>
        <div className="text-xs text-muted-foreground mb-4">
          Personel devam verilerine göre Claude Sonnet 4.6 sizin için sayısal, aksiyona dönük yanıt üretir.
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Örn: En çok geç kalan personel kim?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            data-testid="ai-question"
          />
          <Button onClick={ask} disabled={asking || !question.trim()} className="gap-2" data-testid="ai-send">
            {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {asking ? "Sorguluyor…" : "Sor"}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {suggestedQuestions.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setQuestion(q)}
              className="text-xs px-2.5 py-1 rounded-md border border-border/50 text-muted-foreground hover:text-foreground hover:border-border transition-colors"
              data-testid="ai-suggested"
            >
              <ChevronRight className="w-3 h-3 inline -mt-0.5 mr-0.5" /> {q}
            </button>
          ))}
        </div>

        {answer && (
          <div
            className="mt-4 p-4 rounded-lg bg-secondary/50 text-sm leading-relaxed"
            data-testid="ai-answer"
          >
            {renderInsights(answer)}
          </div>
        )}
      </div>
    </div>
  );
}
